import { useEffect, useRef, useState } from "react";
import {
  Box, Typography, Chip, Divider, IconButton, Tooltip,
  Table, TableBody, TableCell, TableRow, Paper,
} from "@mui/material";
import InfoOutlinedIcon  from "@mui/icons-material/InfoOutlined";
import MyLocationIcon    from "@mui/icons-material/MyLocation";
import ChevronLeftIcon   from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon  from "@mui/icons-material/ChevronRight";
import CloseIcon         from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { createPortal }  from "react-dom";

import OlSelect    from "ol/interaction/Select";
import { click }   from "ol/events/condition";
import Style       from "ol/style/Style";
import Fill        from "ol/style/Fill";
import Stroke      from "ol/style/Stroke";
import CircleStyle from "ol/style/Circle";

// ── Highlight style ───────────────────────────────────────────────────
const HIGHLIGHT = new Style({
  fill:   new Fill({ color: "rgba(255,255,0,0.35)" }),
  stroke: new Stroke({ color: "#00c8ff", width: 3 }),
  image:  new CircleStyle({
    radius: 9,
    fill:   new Fill({ color: "rgba(255,255,0,0.9)" }),
    stroke: new Stroke({ color: "#00c8ff", width: 2.5 }),
  }),
});

// ── Helpers ───────────────────────────────────────────────────────────
function getImages(props) {
  const key = Object.keys(props).find(k =>
    k.toLowerCase() === "multiple_images" || k.toLowerCase() === "images"
  );
  if (!key || !props[key]) return [];
  const v = props[key];
  if (typeof v === "string") return v.split(",").map(s => s.trim()).filter(Boolean);
  if (Array.isArray(v))     return v.filter(s => typeof s === "string");
  return [];
}

function fmt(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

// ── Image carousel ────────────────────────────────────────────────────
function ImageCarousel({ images }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => setIdx(0), [images]);
  if (!images?.length) return null;
  return (
    <Box sx={{ position: "relative", width: "100%", height: 160, background: "#0a0a0a", overflow: "hidden", flexShrink: 0 }}>
      <img
        src={images[idx]} alt={`img-${idx}`}
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
        onError={e => {
          e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='60'%3E%3Crect fill='%23222' width='120' height='60'/%3E%3Ctext x='60' y='35' fill='%23666' font-size='11' text-anchor='middle'%3ENo image%3C/text%3E%3C/svg%3E";
        }}
      />
      {images.length > 1 && (
        <>
          <IconButton size="small" onClick={() => setIdx(i => (i - 1 + images.length) % images.length)}
            sx={{ position: "absolute", left: 6, top: "50%", transform: "translateY(-50%)",
              background: "rgba(0,0,0,0.6)", color: "#fff",
              "&:hover": { background: "rgba(0,0,0,0.85)" } }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => setIdx(i => (i + 1) % images.length)}
            sx={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
              background: "rgba(0,0,0,0.6)", color: "#fff",
              "&:hover": { background: "rgba(0,0,0,0.85)" } }}>
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <Chip label={`${idx + 1} / ${images.length}`} size="small"
            sx={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)",
              fontSize: 10, height: 18, background: "rgba(0,0,0,0.65)", color: "#fff",
              border: "1px solid rgba(255,255,255,0.15)" }} />
        </>
      )}
    </Box>
  );
}

// ── Draggable popup ───────────────────────────────────────────────────
const POPUP_W = 300;

function DraggablePopup({ containerEl, onClose, children }) {
  const [pos,      setPos]      = useState({ x: 16, y: 16 });
  const [dragging, setDragging] = useState(false);
  const offset   = useRef({ x: 0, y: 0 });
  const paperRef = useRef(null);

  const onMouseDown = e => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = containerEl?.getBoundingClientRect() ?? { left: 0, top: 0 };
    offset.current = {
      x: e.clientX - rect.left - pos.x,
      y: e.clientY - rect.top  - pos.y,
    };
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = e => {
      if (!containerEl) return;
      const rect   = containerEl.getBoundingClientRect();
      const paperH = paperRef.current?.offsetHeight ?? 420;
      setPos({
        x: Math.max(0, Math.min(e.clientX - rect.left - offset.current.x, rect.width  - POPUP_W)),
        y: Math.max(0, Math.min(e.clientY - rect.top  - offset.current.y, rect.height - paperH)),
      });
    };
    const onUp = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onUp);
    };
  }, [dragging, containerEl]);

  return (
    <Paper
      ref={paperRef}
      elevation={0}
      sx={{
        position:      "absolute",
        left:          pos.x,
        top:           pos.y,
        width:         POPUP_W,
        maxHeight:     "65vh",
        display:       "flex",
        flexDirection: "column",
        borderRadius:  2,
        overflow:      "hidden",
        zIndex:        1400,
        userSelect:    "none",
        border:        "1px solid",
        borderColor:   "divider",
        boxShadow:     "0 8px 32px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.10)",
      }}
    >
      {/* Header / drag handle */}
      <Box
        onMouseDown={onMouseDown}
        sx={{
          display: "flex", alignItems: "center",
          px: 1.25, py: 0.85,
          background: "linear-gradient(90deg,#1565c0 0%,#1976d2 100%)",
          color: "#fff",
          cursor: dragging ? "grabbing" : "grab",
          flexShrink: 0,
          gap: 0.5,
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 16, opacity: 0.7 }} />
        <InfoOutlinedIcon  sx={{ fontSize: 14, opacity: 0.9 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, flex: 1, letterSpacing: 0.3 }}>
          Feature Info
        </Typography>
        <IconButton size="small" onClick={onClose}
          sx={{ color: "#fff", p: 0.3, borderRadius: 1,
            "&:hover": { background: "rgba(255,255,255,0.18)" } }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>

      {children}
    </Paper>
  );
}

// ── Main component ────────────────────────────────────────────────────
export default function FeatureInfoButton({ map, mapContainer }) {
  const [open,    setOpen]    = useState(false);
  const [feature, setFeature] = useState(null);

  const selectRef = useRef(null);
  const mapRef    = useRef(map);
  useEffect(() => { mapRef.current = map; }, [map]);

  // Resolve to DOM element — accept ref object or raw element
  const containerEl = mapContainer?.current ?? mapContainer ?? null;

  // ── OL Select interaction ──
  useEffect(() => {
    const m = mapRef.current;
    if (!m || !open) return;
    const select = new OlSelect({ condition: click, style: () => HIGHLIGHT });
    select.on("select", e => { const f = e.selected[0]; if (f) setFeature(f); });
    m.addInteraction(select);
    selectRef.current = select;
    return () => {
      select.getFeatures().clear();
      m.removeInteraction(select);
      selectRef.current = null;
    };
  }, [open]);

  const handleClose = () => {
    selectRef.current?.getFeatures().clear();
    if (selectRef.current) mapRef.current?.removeInteraction(selectRef.current);
    selectRef.current = null;
    setFeature(null);
    setOpen(false);
  };

  // ── Display data ──
  const props  = feature?.getProperties() ?? null;
  const imgKey = props
    ? Object.keys(props).find(k => k.toLowerCase() === "multiple_images" || k.toLowerCase() === "images")
    : null;
  const images     = props ? getImages(props) : [];
  const attributes = props
    ? Object.entries(props).filter(([k]) => k !== "geometry" && k !== imgKey)
    : [];

  const geomType = feature?.getGeometry()?.getType();
  const GEOM_COLOR = {
    Point: "#2e7d32", MultiPoint: "#2e7d32",
    LineString: "#1565c0", MultiLineString: "#1565c0",
    Polygon: "#6a1b9a", MultiPolygon: "#6a1b9a",
  };

  // ── Popup JSX ──
  const popup = (
    <DraggablePopup containerEl={containerEl} onClose={handleClose}>
      {!feature ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", py: 5, gap: 1.5, px: 2 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: "50%",
            background: "rgba(25,118,210,0.08)", display: "flex",
            alignItems: "center", justifyContent: "center" }}>
            <MyLocationIcon sx={{ fontSize: 26, color: "primary.main", opacity: 0.5 }} />
          </Box>
          <Typography variant="caption" color="text.disabled" align="center" sx={{ lineHeight: 1.6 }}>
            Click any feature on the map<br />to inspect its attributes
          </Typography>
        </Box>
      ) : (
        <>
          <ImageCarousel images={images} />

          <Box sx={{ px: 1.5, py: 1, display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary", flex: 1,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {feature.get("name") ?? feature.get("title") ?? "Feature"}
            </Typography>
            {geomType && (
              <Chip label={geomType} size="small" sx={{
                fontSize: 9, height: 17, fontWeight: 600,
                background: `${GEOM_COLOR[geomType] ?? "#555"}18`,
                color:      GEOM_COLOR[geomType] ?? "#555",
                border:     `1px solid ${GEOM_COLOR[geomType] ?? "#555"}40`,
              }} />
            )}
          </Box>

          <Divider />

          <Box sx={{ overflowY: "auto", flex: 1,
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": { borderRadius: 2, background: "rgba(0,0,0,0.18)" },
          }}>
            {attributes.length === 0 ? (
              <Typography variant="caption" color="text.disabled"
                sx={{ display: "block", textAlign: "center", py: 3 }}>
                No attributes
              </Typography>
            ) : (
              <Table size="small" sx={{ tableLayout: "fixed" }}>
                <TableBody>
                  {attributes.map(([key, val], i) => (
                    <TableRow key={key} sx={{
                      "&:nth-of-type(odd)": { background: "rgba(0,0,0,0.025)" },
                      "&:hover":            { background: "rgba(25,118,210,0.06)" },
                    }}>
                      <TableCell sx={{
                        fontWeight: 600, fontSize: 11, color: "text.secondary",
                        whiteSpace: "nowrap", width: "42%", py: 0.6,
                        overflow: "hidden", textOverflow: "ellipsis",
                        borderBottom: i === attributes.length - 1 ? "none" : undefined,
                      }}>
                        {key}
                      </TableCell>
                      <TableCell sx={{
                        fontSize: 11, py: 0.6, wordBreak: "break-word", color: "text.primary",
                        borderBottom: i === attributes.length - 1 ? "none" : undefined,
                      }} title={fmt(val)}>
                        {fmt(val)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </>
      )}
    </DraggablePopup>
  );

  return (
    <>
      {/* Toggle button — stays inside the button stack */}
      <Tooltip title="Feature Info" placement="left">
        <IconButton
          onClick={() => setOpen(o => !o)}
          size="small"
          sx={{
            width: 40, height: 40, borderRadius: 1,
            backgroundColor: open ? "primary.main" : "background.paper",
            color:           open ? "#fff"          : "text.primary",
            border: "1px solid",
            borderColor: open ? "primary.main" : "divider",
            boxShadow: 1,
            "&:hover": { backgroundColor: open ? "primary.dark" : "grey.100" },
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      {/* Popup — portalled into the map Paper so position:absolute is correct */}
      {open && containerEl && createPortal(popup, containerEl)}
    </>
  );
}